import { useEffect } from 'react';
import { Link, useRouterState } from '@tanstack/react-router';

import { usePendingDisplay } from '@/shared/hooks/usePendingDisplay';
import { PendingRegion } from '@/shared/components/PendingRegion';
import { NotFoundState } from '@/shared/components/NotFoundState';
import { Skeleton } from '@/components/ui/skeleton';
import {
  BoardCanvasLazy,
  RunConversation,
  RunPrompt,
  TaskDrawer,
  locateTask,
  stepLabelsOf,
} from '@/features/tasks';
import { SchedulerStatus, type TaskTab } from '@/features/tasks';
import { useBreadcrumbOverridesStore } from '@/features/app-shell/breadcrumbOverridesStore';

import { usePreferencesStore } from '@/shared/store/preferences';

import { buildActivityFeed } from '../activityFeed';
import { useLinkedRun } from '../hooks/useLinkedRun';
import { useRunActivity } from '../hooks/useRunActivity';
import { runActivity } from '../runActivity';
import type { RunView } from '../searchParams';
import { LinkedRunEmptyState } from './LinkedRunEmptyState';
import { RunActivity } from './RunActivity';
import { RunHeader } from './RunHeader';
import { RunProgress } from './RunProgress';
import { RunStatusBanner } from './RunStatusBanner';
import { RunTaskList } from './RunTaskList';
import { RunViewToggle } from './RunViewToggle';

export interface LinkedRunPageProps {
  workspaceId: string;
  /** The *parent* task's own workflow/id — the target workflow is never in the address (FR-004). */
  workflowKey: string;
  taskId: string;
  /** The drawer's own task, opened over this board via the same `?task=` mechanism `board.tsx` uses. */
  openTaskId?: string;
  openTab?: TaskTab;
  /** A specific run of the parent (`?execution=`); absent means the latest. */
  executionId?: string;
  /**
   * The tasks as a compact board or a list, as the address names it. Absent
   * means the board — the run's own workflow, the same way `BoardPage`
   * itself always opens on the board rather than a list.
   */
  view?: RunView;
  onViewChange?: (view: RunView) => void;
  onOpenTask?: (taskId: string) => void;
  onCloseTask?: () => void;
  onTabChange?: (tab: TaskTab, options?: { replace?: boolean }) => void;
  /** The file shown beside the open task, from the address; the drawer's. */
  previewPath?: string;
  /** Takes `preview` out of the address; required, as the drawer's is. */
  onPreviewClose: () => void;
}

/**
 * One `linked_workflow` execution's created tasks, presented as a real board
 * (spec 115 — design doc). Reuses `BoardCanvasLazy`/`TaskDrawer` unchanged:
 * the tasks this page shows are ordinary `TaskView`s on the target workflow's
 * own steps, so dragging one, opening its drawer, and moving it between
 * columns all work exactly as they do on that workflow's real board — there
 * is no separate "run board" interaction model to build or maintain.
 */
export function LinkedRunPage({
  workspaceId,
  workflowKey,
  taskId,
  openTaskId,
  openTab,
  executionId,
  view: requestedView,
  onViewChange,
  onOpenTask,
  onCloseTask,
  onTabChange,
  previewPath,
  onPreviewClose,
}: LinkedRunPageProps) {
  const { isPending, workspace, parentTask, resolution } = useLinkedRun(
    workspaceId,
    workflowKey,
    taskId,
    executionId,
  );
  const showPlaceholder = usePendingDisplay(isPending);
  // Who changed the run's subtasks (spec 148). A failed read leaves `entries`
  // empty, so the progress events below still show.
  const { entries: activityEntries } = useRunActivity(workspaceId, taskId);
  const showProgress = usePreferencesStore((s) => s.showRunProgressEvents);
  const setShowProgress = usePreferencesStore(
    (s) => s.setShowRunProgressEvents,
  );

  // The design doc's breadcrumb wording ("Workflows › <workflow name> ›
  // <task title> › Run", §4.2) needs the *parent* workflow's name and this
  // task's title — both already in hand here, so this page supplies them to
  // the shell's ambient `Breadcrumbs` rather than that pure, pathname-only
  // function trying to fetch them itself (research.md Decision 6).
  const pathname = useRouterState({
    select: (state) => state.location.pathname,
  });
  const setBreadcrumbOverrides = useBreadcrumbOverridesStore(
    (state) => state.set,
  );
  const clearBreadcrumbOverrides = useBreadcrumbOverridesStore(
    (state) => state.clear,
  );
  const parentWorkflowName = workspace?.workflows.find(
    (candidate) => candidate.key === workflowKey,
  )?.name;
  useEffect(() => {
    if (!parentTask || !parentWorkflowName) return;
    setBreadcrumbOverrides(pathname, {
      labels: { [workflowKey]: parentWorkflowName, [taskId]: parentTask.title },
      omit: new Set(['tasks']),
    });
    return () => clearBreadcrumbOverrides(pathname);
  }, [
    pathname,
    workflowKey,
    taskId,
    parentTask,
    parentWorkflowName,
    setBreadcrumbOverrides,
    clearBreadcrumbOverrides,
  ]);

  if (isPending) {
    if (!showPlaceholder) return null;
    return (
      <section className="flex flex-1 flex-col gap-6 max-md:px-4 max-md:pt-4 p-6">
        <PendingRegion label="Loading run" className="gap-4">
          <Skeleton className="h-6 w-48 rounded-md" />
          <Skeleton className="flex-1 rounded-lg" />
        </PendingRegion>
      </section>
    );
  }

  // `useWorkspace` resolves to `null` on a failed read rather than throwing.
  if (workspace === null) {
    return (
      <section className="flex flex-1 flex-col items-center justify-center gap-3 p-10 text-center">
        <p className="text-foreground text-sm font-medium">
          The run could not be loaded.
        </p>
        <p className="text-muted-foreground max-w-prose text-sm">
          Something went wrong reading this workspace. Try reloading the page.
        </p>
      </section>
    );
  }

  // The address names a task this workspace does not have — a not-found
  // condition (a path parameter names a resource), distinct from `resolution`
  // reporting the task exists but is not currently on an open join.
  if (!parentTask) {
    return (
      <NotFoundState
        title="Task not found"
        description="This workspace has no task with that id. It may have been removed since the link was shared."
      />
    );
  }

  if (!resolution || resolution.status === 'not-linked') {
    return (
      <LinkedRunEmptyState
        workspaceId={workspaceId}
        workflowKey={workflowKey}
        taskId={taskId}
      />
    );
  }

  const { summary, board } = resolution;
  const stepLabels = stepLabelsOf(board);
  const startStepId = board.columns[0]?.steps[0]?.stepId ?? '';
  const targetWorkflow = workspace?.workflows.find(
    (candidate) => candidate.id === summary.targetWorkflowId,
  );

  // Board by default, always — the run's own workflow, not a size-based
  // guess. List stays one click (or `?view=list`) away via the toggle below.
  const view: RunView = requestedView ?? 'board';

  return (
    // `min-w-0`: the compact board's row is `w-max`, and without it this
    // section took that width as its own, pushing the progress bar and the
    // view toggle past the window's right edge. The board scrolls sideways
    // inside its own canvas instead.
    <section className="relative flex min-h-0 min-w-0 flex-1 flex-col gap-6 max-md:px-4 max-md:pt-4 p-6">
      <RunHeader
        workspaceId={workspaceId}
        workflowKey={workflowKey}
        parentWorkflowName={parentWorkflowName ?? workflowKey}
        summary={summary}
        parent={parentTask}
      />

      {/*
        Two columns on a wide screen: the tasks, and beside them what has
        happened. One column ran the activity feed below the board, out of
        sight, while a third of the window stood empty to the right.
      */}
      <div className="grid min-w-0 gap-6 xl:grid-cols-[minmax(0,1fr)_20rem]">
        <div className="flex min-w-0 flex-col gap-6">
          {/*
            Progress first — "is it moving" is the question this page is
            opened to answer. Rolled up from this run's own filtered task set
            (research.md, data-model.md) — the same `done`/`total`
            `boardCompletion` reports for the board rendered below, not a
            second copy of it. Not `BoardProgress`: FD-16 reserves that
            gradient for the board page.
          */}
          <RunProgress summary={summary} />

          {/* What the scheduler is doing with the tasks (spec 239 US1). */}
          {summary.closed ? null : (
            <SchedulerStatus scheduler={summary.scheduler} />
          )}

          {/* What needs acting on, or that nothing does any more. */}
          <RunStatusBanner
            summary={summary}
            tasks={resolution.tasks}
            board={board}
            onOpenTask={onOpenTask}
          />

          {/*
            Not `flex-1`: this page has content below the board on a narrow
            screen. As a flexible child the section shrank to whatever the page
            had left and the board painted over the activity feed. Sized by its
            content (the compact board sizes to its cards), the page grows and
            the shell's `<main>` scrolls.
          */}
          <section
            aria-label="Tasks"
            className="flex min-w-0 shrink-0 flex-col gap-3"
          >
            <div className="flex flex-wrap items-center justify-between gap-3">
              <h2 className="text-foreground text-sm font-medium">Tasks</h2>
              <div className="flex items-center gap-3">
                {/*
                  Said, not left to be discovered: on a closed run a drag does
                  nothing, and a board that silently refuses reads as broken.
                */}
                {summary.closed && view === 'board' ? (
                  <span className="text-muted-foreground text-xs">
                    Run closed. Move tasks from their own board.
                  </span>
                ) : null}
                <RunViewToggle
                  view={view}
                  onChange={(next) => onViewChange?.(next)}
                />
              </div>
            </div>

            {view === 'list' && targetWorkflow ? (
              <RunTaskList
                targetWorkflow={targetWorkflow}
                tasks={resolution.tasks}
                onOpenTask={onOpenTask}
                split={
                  summary.closed
                    ? undefined
                    : {
                        workspaceId,
                        parent: { id: parentTask.id, title: parentTask.title },
                      }
                }
              />
            ) : (
              <BoardCanvasLazy
                workspaceId={workspaceId}
                workflowId={summary.targetWorkflowId}
                workflowKey={summary.targetWorkflowKey}
                board={board}
                createStepId={startStepId}
                compact
                // Creating a task directly on a run board is not a scenario
                // this feature covers (design doc): this run is scoped to
                // exactly what one execution created, and a manually added task
                // would never belong to it. `onCreateTask` is required by
                // `BoardCanvasProps`, so it is wired to a no-op rather than
                // making the prop optional there for this one caller.
                onCreateTask={() => {
                  /* no-op: the run page never offers task creation. */
                }}
                onOpenTask={onOpenTask}
                // A finished run is shown as it ended. Its tasks are still
                // ordinary tasks — movable on their own workflow's board — but
                // a move here would read as part of a run that is over.
                readOnly={summary.closed}
              />
            )}
          </section>

          {/*
            The run's own Prompt and Conversation (spec 149 FR-001), as the
            parent's Runs tab shows them under this run. A `linked_workflow`
            run had an agent — the one that created these tasks — so it can be
            asked why, once it has finished. Bounded to a reading width: the
            board above may span the window, a thread should not.
          */}
          <section
            aria-label="This run"
            className="flex max-w-3xl min-w-0 flex-col gap-3"
          >
            <RunPrompt
              workspaceId={workspaceId}
              taskId={parentTask.id}
              executionId={summary.execution.id}
            />
            <RunConversation
              workspaceId={workspaceId}
              taskId={parentTask.id}
              execution={summary.execution}
            />
          </section>
        </div>

        {/*
          Computed per render, not memoised: this sits below the early returns,
          where a hook cannot, and it is one pass over a handful of tasks.
        */}
        <RunActivity
          feed={buildActivityFeed(
            runActivity(summary.execution, resolution.tasks, stepLabels),
            activityEntries,
            { now: new Date(), showProgress },
          )}
          showProgress={showProgress}
          onShowProgressChange={setShowProgress}
          onOpenTask={onOpenTask}
          scheduler={summary.closed ? null : { workspaceId, taskId }}
        />
      </div>

      {openTaskId ? (
        <TaskDrawer
          key={openTaskId}
          taskId={openTaskId}
          workspaceId={workspaceId}
          // Which run this task belongs to — what the demo's sub-task sheet
          // said with "parent › step" and the ordinary drawer cannot know.
          context={
            <>
              <Link
                to="/workspaces/$workspaceId/workflows/$workflowKey/board"
                params={{ workspaceId, workflowKey }}
                search={{ task: parentTask.id }}
                className="hover:text-foreground underline-offset-4 hover:underline"
              >
                {parentTask.title}
              </Link>
              <span aria-hidden="true"> › </span>
              {summary.stepName}
            </>
          }
          // The drawer's own task lives on the *target* workflow (this run's
          // board), not this page's own `workflowKey` prop (the parent
          // task's workflow) — the same distinction FR-004 draws for the
          // route itself.
          workflowKey={summary.targetWorkflowKey}
          task={resolution.tasks.find(
            (candidate) => candidate.id === openTaskId,
          )}
          location={locateTask(board, openTaskId)}
          stepLabels={stepLabels}
          labelRegistry={workspace?.labels ?? []}
          tab={openTab}
          onTabChange={(tab, options) => onTabChange?.(tab, options)}
          onClose={() => onCloseTask?.()}
          previewPath={previewPath}
          onPreviewClose={onPreviewClose}
        />
      ) : null}
    </section>
  );
}
